import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { useDataProvider } from 'react-admin';
import { TextField, Select, MenuItem, FormControl, InputLabel, Typography, Paper, Grid, Alert, IconButton, Box, Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import { usePriorContext } from '@/components/employee/usePriorContext';
import type { EmployeeSearchResult } from '@/components/employee';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { buildTransitionChain, useRuleEvaluation } from '@/components/workflow';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
  source?: 'employee' | 'relatedRecord';
}

const STEPS = [
  { id: 'employee', title: 'Employee & Incident' },
  { id: 'warningType', title: 'Warning Type' },
  { id: 'notice', title: 'Notice to Attend' },
  { id: 'hearing', title: 'Hearing Outcome' },
  { id: 'issue', title: 'Issue Warning' },
];

const WARNING_TYPES = ['Verbal', 'Written', 'FinalWritten'] as const;
const WARNING_TYPE_LABELS: Record<string, string> = {
  Verbal: 'Verbal Warning',
  Written: 'Written Warning',
  FinalWritten: 'Final Written Warning',
};
const OUTCOMES = ['Guilty', 'NotGuilty', 'Postponed'] as const;
const OUTCOME_LABELS: Record<string, string> = {
  Guilty: 'Guilty',
  NotGuilty: 'Not Guilty',
  Postponed: 'Postponed',
};
const SANCTIONS = ['Verbal Warning', 'Written Warning', 'Final Written Warning', 'Suspend Without Pay', 'Dismissal'] as const;

interface FormValues {
  employeeName: string;
  employeeId: string;
  incidentDate: string;
  description: string;
  warningType: string;
  hearingDate: string;
  hearingLocation: string;
  charges: string[];
  consequences: string;
  representative: string;
  noticeSentDate: string;
  outcome: string;
  sanction: string;
  minutes: string;
  issueDate: string;
  validityMonths: number;
  misconductDescription: string;
  hearingMinutes: string;
  witnessStatements: string[];
  employeeAcknowledged: boolean;
  outcomeNotes: string;
}

const INITIAL_VALUES: FormValues = {
  employeeName: '', employeeId: '', incidentDate: '', description: '',
  warningType: '', hearingDate: '', hearingLocation: '', charges: [],
  consequences: '', representative: '', noticeSentDate: '', outcome: '', sanction: '',
  minutes: '', issueDate: '', validityMonths: 6,
  misconductDescription: '', hearingMinutes: '', witnessStatements: [],
  employeeAcknowledged: false, outcomeNotes: '',
};

const stepSchemas = {
  employee: z.object({
    employeeName: z.string().min(1, 'Employee name is required'),
    employeeId: z.string().min(1, 'Employee number is required'),
    incidentDate: z.string().min(1, 'Incident date is required'),
    description: z.string().min(20, 'Description must be at least 20 characters'),
  }),
  warningType: z.object({
    warningType: z.string().min(1, 'Warning type is required'),
  }),
  notice: z.object({
    hearingDate: z.string().min(1, 'Hearing date is required'),
    hearingLocation: z.string().min(1, 'Hearing location is required'),
    charges: z.array(z.string()).min(1, 'At least one charge is required'),
  }),
  hearing: z.object({
    outcome: z.string().min(1, 'Outcome is required'),
  }),
  issue: z.object({
    issueDate: z.string().min(1, 'Issue date is required'),
  }),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  const result = stepValidator(schema)(values as unknown as Record<string, string>);
  if (stepId === 'hearing' && values.outcome === 'Guilty' && !values.sanction) {
    result.errors.sanction = 'Sanction is required when guilty';
    result.isValid = false;
  }
  if (stepId === 'notice' && values.hearingDate) {
    const hearingDateObj = new Date(values.hearingDate);
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + 48);
    if (hearingDateObj < minDate) {
      result.errors.hearingDate = 'Hearing must be scheduled at least 48 hours from now';
      result.isValid = false;
    }
  }
  return result;
};

export const DisciplinaryWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);
  const [currentCharge, setCurrentCharge] = useState('');

  const priorData = usePriorContext({
    dataProvider,
    employeeId: selectedEmployee?.employeeNumber || '',
  });

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      caseId: null,
      employeeId: v.employeeId,
      incidentDate: v.incidentDate,
      misconductDescription: v.description,
      warningType: v.warningType,
      validityMonths: v.validityMonths,
      issuedAt: v.issueDate || null,
      expiryDate: v.issueDate ? new Date(new Date(v.issueDate).setMonth(new Date(v.issueDate).getMonth() + v.validityMonths)).toISOString() : null,
      hearingDate: v.hearingDate,
      hearingLocation: v.hearingLocation,
      hearingMinutes: v.hearingMinutes || v.minutes,
      witnessStatements: v.witnessStatements || [],
      employeeAcknowledged: v.employeeAcknowledged,
      employeeAcknowledgedAt: v.employeeAcknowledged ? new Date().toISOString() : null,
      outcome: v.outcome,
      sanction: v.sanction,
      outcomeNotes: v.outcomeNotes || v.minutes,
    };

    const caseResult = await dataProvider.create('disciplinary-cases', { data: formValues });
    const caseId = caseResult.data.id;

    const wizardEvents = ['SCHEDULE_HEARING', 'RECORD_OUTCOME'];
    const { finalState: hearingState, transitions: hearingTransitions } = buildTransitionChain('disciplinarywarning', 'pending', wizardEvents);

    let finalState = hearingState;
    const transitions = [...hearingTransitions];

    if (v.outcome === 'Guilty' && hearingState !== 'warning_issued') {
      const { finalState: guiltyState, transitions: guiltyTransitions } = buildTransitionChain('disciplinarywarning', hearingState, ['FOUND_GUILTY']);
      finalState = guiltyState;
      transitions.push(...guiltyTransitions);
    } else if (v.outcome === 'NotGuilty' && hearingState !== 'dismissed') {
      const { finalState: notGuiltyState, transitions: notGuiltyTransitions } = buildTransitionChain('disciplinarywarning', hearingState, ['FOUND_NOT_GUILTY']);
      finalState = notGuiltyState;
      transitions.push(...notGuiltyTransitions);
    }

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'disciplinarywarning',
        entityId: caseId,
        entityName: `Disciplinary: ${v.employeeName}`,
        currentState: { value: finalState, context },
        status: finalState === 'warning_issued' || finalState === 'dismissed' ? 'completed' : 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: caseId,
        transitions,
      },
    });

    return { id: caseId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    setFieldErrors,
    clearErrors,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: { ...INITIAL_VALUES, employeeId: employeeId || '' },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'disciplinary-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      warningType: (values as unknown as FormValues).warningType,
      employeeId: (values as unknown as FormValues).employeeId,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'issue') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const handleEmployeeSelect = useCallback((employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);

    const autofillData = {
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    };

    const fields: PrefilledField[] = [
      { field: 'employeeName', label: 'Employee Name', sourceValue: employee.fullName, currentValue: employee.fullName, confirmed: true },
      { field: 'employeeId', label: 'Employee ID', sourceValue: employee.employeeNumber, currentValue: employee.employeeNumber, confirmed: true },
    ];

    setPrefilledFields(fields);
    updateValues(autofillData);
  }, [updateValues]);

  const handleClearEmployee = useCallback(() => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
  }, []);

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const vals = values as unknown as FormValues;

  const addCharge = () => {
    if (currentCharge.trim()) {
      const newCharges = [...vals.charges, currentCharge.trim()];
      updateField('charges', newCharges);
      setCurrentCharge('');
    }
  };

  const removeCharge = (index: number) => {
    const newCharges = vals.charges.filter((_: string, i: number) => i !== index);
    updateField('charges', newCharges);
  };

  const handleChargeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCharge();
    }
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, vals);
    if (!result.isValid) {
      setFieldErrors(result.errors);
      return;
    }
    clearErrors();
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleReset = () => {
    reset();
  };

  const renderFormFields = (stepId: string): React.ReactNode => {
    switch (stepId) {
      case 'employee':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <EmployeeSearchAutocomplete
                label="Search Employee"
                onEmployeeSelect={handleEmployeeSelect}
                onClear={handleClearEmployee}
              />
            </Grid>
            {selectedEmployee && priorData.priorWarnings.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  {selectedEmployee.fullName} has {priorData.priorWarnings.length} prior warning(s).
                  Latest: {priorData.priorWarnings[0].warningType || 'Unknown'} — {priorData.priorWarnings[0].incidentDate}
                  {priorData.priorWarnings.length >= 2 ? ' Consider escalation to Final Written Warning.' : ''}
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Employee Name" value={vals.employeeName}
                onChange={e => updateField('employeeName', e.target.value)} fullWidth
                error={!!errors.employeeName} helperText={errors.employeeName} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Employee #" value={vals.employeeId}
                onChange={e => updateField('employeeId', e.target.value)} fullWidth
                error={!!errors.employeeId} helperText={errors.employeeId} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Incident Date" type="date" value={vals.incidentDate}
                onChange={e => updateField('incidentDate', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} error={!!errors.incidentDate}
                helperText={errors.incidentDate || 'Date when incident occurred'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Incident Description" value={vals.description}
                onChange={e => updateField('description', e.target.value)} fullWidth
                multiline rows={4} error={!!errors.description}
                helperText={errors.description || 'Describe the alleged misconduct (min 20 characters)'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                LRA Schedule 8 requires that employees receive notice of charges and right to be accompanied
              </Alert>
            </Grid>
          </Grid>
        );

      case 'warningType':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth error={!!errors.warningType}>
                <InputLabel>Warning Type</InputLabel>
                <Select value={vals.warningType} label="Warning Type"
                  onChange={e => {
                    updateField('warningType', e.target.value);
                    if (e.target.value === 'Written') updateField('validityMonths', 6);
                    if (e.target.value === 'FinalWritten') updateField('validityMonths', 12);
                  }}>
                  {WARNING_TYPES.map(w => <MenuItem key={w} value={w}>{WARNING_TYPE_LABELS[w] ?? w}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.warningType && <Typography variant="caption" color="error">{errors.warningType}</Typography>}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Warning Validity Periods</Typography>
                <Typography variant="body2">• Verbal: No formal validity period</Typography>
                <Typography variant="body2">• Written: 6 months — auto-archived after validity</Typography>
                <Typography variant="body2">• Final Written: 12 months — auto-archived after validity</Typography>
              </Paper>
            </Grid>
          </Grid>
        );

      case 'notice':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Scheduled Hearing Date" type="date" value={vals.hearingDate}
                onChange={e => updateField('hearingDate', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} error={!!errors.hearingDate}
                helperText={errors.hearingDate || 'Must be at least 48 hours from now'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Hearing Location" value={vals.hearingLocation}
                onChange={e => updateField('hearingLocation', e.target.value)} fullWidth
                error={!!errors.hearingLocation} helperText={errors.hearingLocation} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Charges *</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="Add charge"
                  value={currentCharge}
                  onChange={e => setCurrentCharge(e.target.value)}
                  onKeyDown={handleChargeKeyDown}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter a charge (e.g., Absent from work without permission for three consecutive days)"
                  error={!!errors.charges}
                  helperText={errors.charges}
                />
                <IconButton color="primary" onClick={addCharge} disabled={!currentCharge.trim()} sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <Add />
                </IconButton>
              </Box>
              {vals.charges.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ width: 60, fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Charge</TableCell>
                        <TableCell sx={{ width: 80 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vals.charges.map((charge: string, index: number) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>{index + 1}</TableCell>
                          <TableCell sx={{ py: 1 }}>{charge}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeCharge(index)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No charges added yet. Enter a charge above and click + to add.
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Potential Consequences"
                value={vals.consequences}
                onChange={e => updateField('consequences', e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Describe potential consequences if charges are proven (e.g., disciplinary action up to and including dismissal)"
                helperText="Per LRA Schedule 8, employee must be notified of potential consequences"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Notice Sent Date" type="date" value={vals.noticeSentDate}
                onChange={e => updateField('noticeSentDate', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Employee Representative (Union rep or colleague)"
                value={vals.representative}
                onChange={e => updateField('representative', e.target.value)} fullWidth
                placeholder="e.g., NUFAWU representative" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Notice must include: charges, date/time/place of hearing, right to be accompanied,
                potential consequences per LRA Schedule 8
              </Alert>
            </Grid>
          </Grid>
        );

      case 'hearing':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.outcome}>
                <InputLabel>Outcome</InputLabel>
                <Select value={vals.outcome} label="Outcome"
                  onChange={e => updateField('outcome', e.target.value)}>
                  {OUTCOMES.map(o => <MenuItem key={o} value={o}>{OUTCOME_LABELS[o] ?? o}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.outcome && <Typography variant="caption" color="error">{errors.outcome}</Typography>}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.sanction} disabled={vals.outcome !== 'Guilty'}>
                <InputLabel>Sanction (if Guilty)</InputLabel>
                <Select value={vals.sanction} label="Sanction (if Guilty)"
                  onChange={e => updateField('sanction', e.target.value)}>
                  {SANCTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.sanction && <Typography variant="caption" color="error">{errors.sanction}</Typography>}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Hearing Minutes / Summary" value={vals.minutes}
                onChange={e => updateField('minutes', e.target.value)} fullWidth
                multiline rows={4} placeholder="Record key points from the hearing..." />
            </Grid>
          </Grid>
        );

      case 'issue':
        return (
          <Grid container spacing={2}>
            {ruleResult && !ruleResult.allowed && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Guard Check</Typography>
                  <Typography variant="body2">{ruleResult.reason}</Typography>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Issue Date" type="date" value={vals.issueDate}
                onChange={e => updateField('issueDate', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} error={!!errors.issueDate}
                helperText={errors.issueDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Validity Period (months)"
                type="number"
                value={vals.validityMonths}
                onChange={e => updateField('validityMonths', parseInt(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={vals.warningType === 'Written' ? 'Default: 6 months' : vals.warningType === 'FinalWritten' ? 'Default: 12 months' : 'N/A for Verbal'}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">
                Warning will be valid for {vals.validityMonths} months and auto-archived thereafter
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onNext={handleNext}
      onComplete={complete}
      onReset={handleReset}
    >
      {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}

      {isCompleted ? (
        <OutcomeSummary
          title="Disciplinary Case Initiated"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee', value: vals.employeeName },
            { label: 'Employee #', value: vals.employeeId },
            { label: 'Warning Type', value: vals.warningType },
            { label: 'Incident Date', value: vals.incidentDate },
            { label: 'Hearing Date', value: vals.hearingDate },
            { label: 'Outcome', value: vals.outcome || 'Pending' },
          ]}
          footerActions={[
            { label: 'View Case', to: '/disciplinary-cases' },
            { label: 'Start Another', onClick: handleReset },
          ]}
        />
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Step {currentStepIndex + 1} of {totalSteps}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {currentStep.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getStepDescription(currentStep.id)}
          </Typography>

          {renderFormFields(currentStep.id)}
        </Paper>
      )}
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    employee: 'Capture employee and incident details',
    warningType: 'Select the appropriate warning type per LRA Schedule 8',
    notice: 'Generate notice to attend disciplinary hearing',
    hearing: 'Record the hearing outcome and sanction if applicable',
    issue: 'Issue the formal warning and set validity period',
  };
  return descriptions[stepId] || '';
}